import { sha256Canonical, type CoasterProject, type SuccessfulTestCertification } from '../schema';

export const RIDE_CERTIFICATION_VERSION = 1 as const;
export const RIDE_COMPILER_VERSION = 'bc-track-compiler-v1';
export const RIDE_PHYSICS_VERSION = 'bc-constrained-ride-v1';

export type RecordingAccess = 'editable' | 'readonly_public' | 'readonly_private';

export async function createRideCriticalFingerprint(project: CoasterProject): Promise<string> {
  return sha256Canonical({
    version: RIDE_CERTIFICATION_VERSION,
    compilerVersion: RIDE_COMPILER_VERSION,
    physicsVersion: RIDE_PHYSICS_VERSION,
    coasterFamilyId: project.coasterFamilyId,
    track: project.track,
    train: {
      familyId: project.train.familyId,
      carCount: project.train.carCount,
      carSpacingMeters: project.train.carSpacingMeters,
      emptyMassKg: project.train.emptyMassKg,
      capacityPerCar: project.train.capacityPerCar,
    },
  });
}

export function createSuccessfulTestCertification(
  fingerprint: string,
  testedAt = new Date().toISOString(),
): SuccessfulTestCertification {
  return {
    version: RIDE_CERTIFICATION_VERSION,
    fingerprint,
    testedAt,
    compilerVersion: RIDE_COMPILER_VERSION,
    physicsVersion: RIDE_PHYSICS_VERSION,
  };
}

export function isSuccessfulTestCurrent(
  certification: SuccessfulTestCertification | undefined,
  fingerprint: string | null,
): boolean {
  return Boolean(
    certification &&
    fingerprint &&
    certification.version === RIDE_CERTIFICATION_VERSION &&
    certification.fingerprint === fingerprint &&
    certification.compilerVersion === RIDE_COMPILER_VERSION &&
    certification.physicsVersion === RIDE_PHYSICS_VERSION,
  );
}

export function recordingEligibility(
  access: RecordingAccess,
  certification: SuccessfulTestCertification | undefined,
  fingerprint: string | null,
): {
  allowed: boolean;
  source: 'successful_test' | 'approved_public' | null;
  reason: string | null;
} {
  if (access === 'readonly_public')
    return { allowed: true, source: 'approved_public', reason: null };
  if (isSuccessfulTestCurrent(certification, fingerprint))
    return { allowed: true, source: 'successful_test', reason: null };
  return {
    allowed: false,
    source: null,
    reason:
      access === 'readonly_private'
        ? 'This shared draft must be successfully tested by its creator before it can be recorded.'
        : 'Complete a successful Test Ride for this exact build before recording.',
  };
}
